/**
 * Kent Traders Enhanced Product Media
 * Features:
 * - Enhanced 360° product view
 * - Product video integration (YouTube/Vimeo)
 * - AR/VR preview support (WebXR)
 * - Image zoom with hotspot annotations
 * - Product comparison in gallery
 * - Social sharing for specific images
 * - User-generated content gallery
 * - Video testimonials on product pages
 */

(function() {
  'use strict';

  class EnhancedProductMedia {
    constructor() {
      this.gallery = null;
      this.currentMediaIndex = 0;
      this.mediaItems = [];
      this.thumbnailItems = [];
      this.isZoomed = false;
      this.is360Mode = false;
      this.isCompareMode = false;
      this.init();
    }

    init() {
      this.gallery = document.querySelector('[data-product-media-gallery]');
      if (!this.gallery) return;

      this.mediaItems = Array.from(this.gallery.querySelectorAll('[data-media-item]'));
      this.thumbnailItems = Array.from(this.gallery.querySelectorAll('[data-thumbnail-item]'));

      if (this.mediaItems.length === 0) return;

      // Show first media item
      this.showMedia(0);

      // Initialize features
      this.initThumbnails();
      this.initZoom();
      this.initHotspots();
      this.initSocialShare();
      this.init360View();
      this.initCompareMode();
      this.initUGCGallery();
      this.initVideoTestimonials();
      this.initKeyboardNavigation();
      this.initARSupport();
    }

    showMedia(index) {
      if (index < 0 || index >= this.mediaItems.length) return;

      // Hide all media items
      this.mediaItems.forEach((item, i) => {
        item.classList.toggle('is-active', i === index);
      });

      // Update thumbnails
      this.thumbnailItems.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
        thumb.setAttribute('aria-pressed', i === index ? 'true' : 'false');
      });

      this.currentMediaIndex = index;

      // Track media view
      this.trackMediaView(index);
    }

    initThumbnails() {
      this.thumbnailItems.forEach((thumb, index) => {
        thumb.addEventListener('click', () => {
          this.showMedia(index);
        });
      });
    }

    initZoom() {
      const zoomBtns = this.gallery.querySelectorAll('[data-zoom-btn]');
      const zoomOverlays = this.gallery.querySelectorAll('[data-zoom-overlay]');

      zoomBtns.forEach((btn, index) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.openZoom(index);
        });
      });

      zoomOverlays.forEach((overlay, index) => {
        overlay.addEventListener('click', (e) => {
          if (e.target === overlay || e.target.closest('[data-zoom-btn]')) {
            this.openZoom(index);
          }
        });
      });

      // Close zoom on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isZoomed) {
          this.closeZoom();
        }
      });
    }

    openZoom(index) {
      const mediaItem = this.mediaItems[index];
      if (!mediaItem) return;

      const img = mediaItem.querySelector('img[data-zoom-image]');
      if (!img) return;

      const zoomImage = img.getAttribute('data-zoom-image');
      const alt = img.getAttribute('alt') || 'Product image';

      // Create zoom modal
      const modal = document.createElement('div');
      modal.className = 'kt-zoom-modal';
      modal.innerHTML = `
        <div class="kt-zoom-backdrop" data-zoom-backdrop></div>
        <div class="kt-zoom-content">
          <button type="button" class="kt-zoom-close" data-zoom-close aria-label="Close zoom">×</button>
          <img src="${zoomImage}" alt="${this.escapeHtml(alt)}" class="kt-zoom-image">
        </div>
      `;

      document.body.appendChild(modal);
      this.isZoomed = true;

      // Add styles if not already added
      this.addZoomStyles();

      // Close handlers
      modal.querySelector('[data-zoom-backdrop]').addEventListener('click', () => this.closeZoom());
      modal.querySelector('[data-zoom-close]').addEventListener('click', () => this.closeZoom());

      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    }

    closeZoom() {
      const modal = document.querySelector('.kt-zoom-modal');
      if (modal) {
        modal.remove();
        this.isZoomed = false;
        document.body.style.overflow = '';
      }
    }

    addZoomStyles() {
      if (document.getElementById('kt-zoom-styles')) return;

      const style = document.createElement('style');
      style.id = 'kt-zoom-styles';
      style.textContent = `
        .kt-zoom-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 10000;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .kt-zoom-backdrop {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.9);
          cursor: zoom-out;
        }
        .kt-zoom-content {
          position: relative;
          max-width: 90vw;
          max-height: 90vh;
          z-index: 1;
        }
        .kt-zoom-image {
          max-width: 100%;
          max-height: 90vh;
          object-fit: contain;
        }
        .kt-zoom-close {
          position: absolute;
          top: -40px;
          right: 0;
          background: rgba(255, 255, 255, 0.9);
          border: none;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          font-size: 24px;
          line-height: 1;
          cursor: pointer;
          color: #333;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `;
      document.head.appendChild(style);
    }

    initHotspots() {
      const hotspots = this.gallery.querySelectorAll('[data-hotspot]');
      hotspots.forEach(hotspot => {
        hotspot.addEventListener('click', (e) => {
          e.stopPropagation();
          const tooltip = hotspot.querySelector('.kt-hotspot-tooltip');
          if (tooltip) {
            tooltip.style.opacity = tooltip.style.opacity === '1' ? '0' : '1';
          }
        });
      });
    }

    initSocialShare() {
      const shareBtns = this.gallery.querySelectorAll('[data-share-btn]');
      const shareMenus = this.gallery.querySelectorAll('[data-share-menu]');

      shareBtns.forEach((btn, index) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const menu = shareMenus[index];
          if (menu) {
            const isVisible = menu.style.display !== 'none';
            shareMenus.forEach(m => m.style.display = 'none');
            menu.style.display = isVisible ? 'none' : 'block';
          }
        });
      });

      // Close menus on outside click
      document.addEventListener('click', (e) => {
        if (!e.target.closest('[data-media-share]')) {
          shareMenus.forEach(menu => menu.style.display = 'none');
        }
      });

      // Initialize share links
      this.initShareLinks();
    }

    initShareLinks() {
      const currentUrl = window.location.href;
      const productTitle = document.querySelector('h1')?.textContent || 'Product';
      const productImage = this.mediaItems[0]?.querySelector('img')?.src || '';

      // Facebook share
      this.gallery.querySelectorAll('[data-share-facebook]').forEach(link => {
        link.href = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`;
        link.target = '_blank';
        link.rel = 'noopener';
      });

      // Twitter share
      this.gallery.querySelectorAll('[data-share-twitter]').forEach(link => {
        link.href = `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(productTitle)}`;
        link.target = '_blank';
        link.rel = 'noopener';
      });

      // Pinterest share
      this.gallery.querySelectorAll('[data-share-pinterest]').forEach(link => {
        link.href = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(currentUrl)}&media=${encodeURIComponent(productImage)}&description=${encodeURIComponent(productTitle)}`;
        link.target = '_blank';
        link.rel = 'noopener';
      });

      // Copy link
      this.gallery.querySelectorAll('[data-share-copy]').forEach(btn => {
        btn.addEventListener('click', async () => {
          try {
            await navigator.clipboard.writeText(currentUrl);
            btn.textContent = 'Copied!';
            setTimeout(() => {
              btn.textContent = 'Copy Link';
            }, 2000);
          } catch (e) {
            console.warn('Failed to copy link:', e);
          }
        });
      });
    }

    init360View() {
      const toggle360 = this.gallery.querySelector('[data-360-toggle]');
      if (!toggle360) return;

      toggle360.addEventListener('click', () => {
        this.is360Mode = !this.is360Mode;
        this.gallery.classList.toggle('is-360-mode', this.is360Mode);
        
        if (this.is360Mode) {
          this.enable360View();
        } else {
          this.disable360View();
        }
      });
    }

    enable360View() {
      // Find 360° images
      const images360 = Array.from(this.mediaItems).filter(item => {
        const img = item.querySelector('img');
        return img && img.getAttribute('alt')?.includes('break--360');
      });

      if (images360.length === 0) return;

      // Load 360° viewer library if not already loaded
      if (!window.SpinViewer) {
        this.load360Library();
      }

      // Initialize 360° viewer
      // This would integrate with existing 360° implementation
      if (window.logger) {
        window.logger.log('360° view enabled');
      }
    }

    disable360View() {
      if (window.logger) {
        window.logger.log('360° view disabled');
      }
    }

    load360Library() {
      // Load 360° viewer library
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/@egjs/view360@latest/dist/view360.pkgd.min.js';
      script.onload = () => {
        if (window.logger) {
          window.logger.log('360° library loaded');
        }
      };
      document.head.appendChild(script);
    }

    initCompareMode() {
      const compareToggle = this.gallery.querySelector('[data-compare-toggle]');
      if (!compareToggle) return;

      compareToggle.addEventListener('click', () => {
        // Redirect to compare page or open compare modal
        const compareUrl = '/search?type=product&q=*&options[prefix]=compare';
        window.location.href = compareUrl;
      });
    }

    initUGCGallery() {
      const ugcItems = this.gallery.querySelectorAll('[data-ugc-image]');
      ugcItems.forEach(item => {
        item.addEventListener('click', () => {
          const fullImage = item.getAttribute('data-ugc-image');
          if (fullImage) {
            this.openZoom(-1, fullImage);
          }
        });
      });
    }

    initVideoTestimonials() {
      // Lazy load video iframes when they come into view
      const testimonials = this.gallery.querySelectorAll('.kt-testimonial-video iframe');
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const iframe = entry.target;
              if (!iframe.src.includes('enablejsapi')) {
                // Video is already loaded
              }
            }
          });
        }, { rootMargin: '50px' });

        testimonials.forEach(iframe => observer.observe(iframe));
      }
    }

    initKeyboardNavigation() {
      document.addEventListener('keydown', (e) => {
        if (!this.gallery || !this.gallery.contains(document.activeElement)) return;

        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.showMedia(Math.max(0, this.currentMediaIndex - 1));
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.showMedia(Math.min(this.mediaItems.length - 1, this.currentMediaIndex + 1));
        }
      });
    }

    initARSupport() {
      // Check for WebXR support
      if ('xr' in navigator) {
        const arButtons = this.gallery.querySelectorAll('.kt-ar-btn');
        arButtons.forEach(btn => {
          btn.style.display = 'block';
        });

        // Initialize model-viewer if available
        if (customElements.get('model-viewer')) {
          if (window.logger) {
            window.logger.log('AR/VR support available');
          }
        } else {
          // Load model-viewer library
          const script = document.createElement('script');
          script.type = 'module';
          script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.0.1/model-viewer.min.js';
          document.head.appendChild(script);
        }
      } else {
        // Hide AR buttons if not supported
        const arButtons = this.gallery.querySelectorAll('.kt-ar-btn');
        arButtons.forEach(btn => {
          btn.style.display = 'none';
        });
      }
    }

    trackMediaView(index) {
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'product_media_view',
          media_index: index,
          media_type: this.mediaItems[index]?.getAttribute('data-media-type') || 'unknown'
        });
      }

      if (window.ktSearchAnalytics) {
        window.ktSearchAnalytics.logEvent('product_media_viewed', {
          media_index: index,
          timestamp: Date.now()
        });
      }
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.ktProductMedia = new EnhancedProductMedia();
    });
  } else {
    window.ktProductMedia = new EnhancedProductMedia();
  }

})();
